import { useState } from "react";

import {
  Button,
  ErrorBanner,
  Input,
  Modal,
  Select,
  Spinner,
} from "@/components/ui";
import { toApiError } from "@/lib/api";
import { useFormat, useT } from "@/lib/i18n";
import { isValidPlate, plateDigits, plateMask, toE164 } from "@/lib/format";
import {
  useAvailability,
  useCreateAppointment,
  useCustomers,
  useProfessionals,
  useServices,
} from "@/lib/queries";
import type { Customer, Me } from "@/lib/types";

type Step = "servico" | "cliente" | "horario";

export default function NewAppointmentModal({
  open,
  onClose,
  me,
  defaultDate,
}: {
  open: boolean;
  onClose: () => void;
  me: Me;
  defaultDate: string;
}) {
  const t = useT();
  const { money, duration, hhmm, phone } = useFormat();
  // Placa, máscara de telefone e E.164 saem do país da OFICINA: é o
  // carro e o cliente dela que estão sendo cadastrados.
  const { region } = me.garage;
  const pais = region.country;
  const [step, setStep] = useState<Step>("servico");
  const [serviceIds, setServiceIds] = useState<number[]>([]);
  const [professionalId, setProfessionalId] = useState<number | "">(
    me.professional_id ?? "",
  );
  const [date, setDate] = useState(defaultDate);
  const [slot, setSlot] = useState<string | null>(null);

  const [search, setSearch] = useState("");
  const [customerId, setCustomerId] = useState<number | null>(null);
  const [newCustomer, setNewCustomer] = useState({ name: "", phone: "" });
  const [vehicleId, setVehicleId] = useState<number | null>(null);
  const [plate, setPlate] = useState("");

  const { data: services } = useServices();
  const { data: professionals } = useProfessionals();
  const { data: customers, isFetching: searching } = useCustomers(search);
  const create = useCreateAppointment();

  const canQueryAvailability =
    step === "horario" && serviceIds.length > 0 && professionalId !== "";
  const { data: availability, isFetching: loadingSlots } = useAvailability(
    {
      professional: professionalId as number,
      services: serviceIds,
      date,
      days: 1,
    },
    canQueryAvailability,
  );

  const slots = availability?.[date] ?? [];
  const chosen = services?.filter((s) => serviceIds.includes(s.id)) ?? [];
  const totalMin = chosen.reduce((n, s) => n + s.duration_minutes, 0);
  const totalPrice = chosen.reduce((n, s) => n + Number(s.price), 0);

  // Serviço de pátio sem placa é ordem de serviço que só se descobre
  // incompleta quando o cliente chega — o backend recusa, e a tela avisa
  // antes em vez de deixar o 409 explicar.
  const exigeVeiculo = chosen.some((s) => s.requires_vehicle);
  const selecionado: Customer | undefined = customers?.results.find(
    (c) => c.id === customerId,
  );
  const frota = selecionado?.vehicles ?? [];
  const veiculoOk = !exigeVeiculo || vehicleId !== null || isValidPlate(plate, pais);

  // Profissional só marca na própria agenda — o backend também barra (403),
  // mas esconder aqui evita o usuário descobrir isso por erro.
  const selectable = me.permissions.manage_others_agenda
    ? professionals
    : professionals?.filter((p) => p.id === me.professional_id);

  const reset = () => {
    setStep("servico");
    setServiceIds([]);
    setSlot(null);
    setCustomerId(null);
    setNewCustomer({ name: "", phone: "" });
    setVehicleId(null);
    setPlate("");
    setSearch("");
  };

  const close = () => {
    reset();
    onClose();
  };

  const submit = () => {
    create.mutate(
      {
        professional: professionalId,
        service_ids: serviceIds,
        starts_at: slot,
        ...(customerId
          ? { customer: customerId }
          : {
              new_customer: {
                name: newCustomer.name,
                phone: toE164(newCustomer.phone, me.garage.region.country),
              },
            }),
        ...(vehicleId
          ? { vehicle: vehicleId }
          : isValidPlate(plate, pais)
            ? { new_vehicle: { plate: plateDigits(plate, region.plate_max_length) } }
            : {}),
      },
      { onSuccess: close },
    );
  };

  const error = create.isError ? toApiError(create.error) : null;

  return (
    <Modal open={open} onClose={close} title={t("modal.newAppointment")} wide>
      <div className="mb-5 flex gap-1">
        {(["servico", "cliente", "horario"] as Step[]).map((s, i) => (
          <div
            key={s}
            className={`h-1 flex-1 rounded-full ${
              ["servico", "cliente", "horario"].indexOf(step) >= i
                ? "bg-nitro-500"
                : "bg-carbon-700"
            }`}
          />
        ))}
      </div>

      {/* ---------------------------------------------------- 1. serviço */}
      {step === "servico" && (
        <div className="space-y-4">
          <div>
            <p className="mb-2 text-sm font-medium text-steel">{t("modal.services")}</p>
            <div className="space-y-2">
              {services
                ?.filter((s) => s.is_active)
                .map((s) => (
                  <label
                    key={s.id}
                    className="flex cursor-pointer items-center gap-3 rounded-[4px] border border-line p-3 hover:bg-carbon-900 has-checked:border-nitro-500 has-checked:bg-nitro-500/10"
                  >
                    <input
                      type="checkbox"
                      className="size-4"
                      checked={serviceIds.includes(s.id)}
                      onChange={(e) =>
                        setServiceIds((prev) =>
                          e.target.checked
                            ? [...prev, s.id]
                            : prev.filter((id) => id !== s.id),
                        )
                      }
                    />
                    <span className="flex-1">
                      <span className="block font-medium">{s.name}</span>
                      <span className="block text-sm text-steel-muted">
                        {duration(s.duration_minutes)} · {money(s.price)}
                      </span>
                    </span>
                  </label>
                ))}
            </div>
          </div>

          <Select
            label={t("modal.technician")}
            value={professionalId}
            onChange={(e) =>
              setProfessionalId(e.target.value ? Number(e.target.value) : "")
            }
          >
            <option value="">{t("common.select")}</option>
            {selectable?.map((p) => (
              <option key={p.id} value={p.id}>
                {p.display_name}
              </option>
            ))}
          </Select>

          {serviceIds.length > 0 && (
            <p className="text-sm text-steel-muted">
              {t("common.total")} <b>{duration(totalMin)}</b> · <b>{money(totalPrice)}</b>
            </p>
          )}

          <Button
            className="w-full"
            disabled={serviceIds.length === 0 || professionalId === ""}
            onClick={() => setStep("cliente")}
          >
            {t("common.continue")}
          </Button>
        </div>
      )}

      {/* ---------------------------------------------------- 2. cliente */}
      {step === "cliente" && (
        <div className="space-y-4">
          <Input
            label={t("modal.findCustomer")}
            placeholder={t("modal.nameOrPhone")}
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setCustomerId(null);
            }}
          />

          {search.length >= 2 && (
            <div className="max-h-48 space-y-1 overflow-y-auto">
              {searching && <Spinner className="mx-auto my-3" />}
              {customers?.results.map((c) => (
                <button
                  key={c.id}
                  onClick={() => {
                    setCustomerId(c.id);
                    // Um carro só: escolher por ele é o certo em 3 de 4
                    // fichas, e poupa um toque no balcão.
                    setVehicleId(c.vehicles.length === 1 ? c.vehicles[0].id : null);
                    setPlate("");
                  }}
                  className={`flex w-full items-center justify-between rounded-[4px] border p-3 text-left transition ${
                    customerId === c.id
                      ? "border-nitro-500 bg-nitro-500/10"
                      : "border-line hover:bg-carbon-900"
                  }`}
                >
                  <span className="min-w-0">
                    <span className="block font-medium">{c.name}</span>
                    <span className="block text-sm text-steel-muted">
                      {phone(c.phone, pais)} · {c.total_visits} visita(s)
                    </span>
                    {c.vehicles.length > 0 && (
                      <span className="mt-1 flex flex-wrap gap-1">
                        {c.vehicles.map((v) => (
                          <span key={v.id} className="plate">
                            {v.formatted_plate}
                          </span>
                        ))}
                      </span>
                    )}
                  </span>
                  {c.no_show_count > 0 && (
                    <span className="text-xs text-red-400">
                      {c.no_show_count} falta(s)
                    </span>
                  )}
                </button>
              ))}
              {!searching && customers?.results.length === 0 && (
                <p className="py-2 text-sm text-steel-muted">
                  {t("modal.noCustomerFound")}
                </p>
              )}
            </div>
          )}

          {!customerId && (
            <div className="space-y-3 rounded-[4px] bg-carbon-900 p-4">
              <p className="text-sm font-medium text-steel">{t("modal.orCreateNow")}</p>
              <Input
                label={t("common.name")}
                value={newCustomer.name}
                onChange={(e) =>
                  setNewCustomer({ ...newCustomer, name: e.target.value })
                }
              />
              <Input
                label={me.garage.region.messaging_label}
                type="tel"
                inputMode="tel"
                placeholder="(11) 99999-9999"
                value={newCustomer.phone}
                onChange={(e) =>
                  setNewCustomer({ ...newCustomer, phone: e.target.value })
                }
              />
            </div>
          )}

          {exigeVeiculo && (
            <div className="space-y-3 rounded-[2px] border border-line bg-carbon-900 p-4">
              <p className="text-sm font-medium text-steel">{t("modal.whichVehicle")}</p>
              {frota.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {frota.map((v) => (
                    <button
                      key={v.id}
                      onClick={() => {
                        setVehicleId(v.id);
                        setPlate("");
                      }}
                      className={`rounded-[2px] border px-3 py-2 text-left text-sm transition ${
                        vehicleId === v.id
                          ? "border-nitro-500 bg-nitro-500/10"
                          : "border-line hover:bg-carbon-700"
                      }`}
                    >
                      <span className="plate">{v.formatted_plate}</span>
                      <span className="ml-2 text-steel-muted">{v.label}</span>
                    </button>
                  ))}
                </div>
              )}
              <Input
                label={frota.length > 0 ? t("modal.otherPlate") : t("modal.plate")}
                placeholder={me.garage.region.plate_example}
                autoCapitalize="characters"
                spellCheck={false}
                value={plate}
                onChange={(e) => {
                  setPlate(plateMask(e.target.value, pais));
                  setVehicleId(null);
                }}
                hint={t("modal.newPlateHint")}
              />
            </div>
          )}

          <div className="flex gap-2">
            <Button variant="secondary" onClick={() => setStep("servico")}>{t("common.back")}</Button>
            <Button
              className="flex-1"
              disabled={
                (!customerId && (!newCustomer.name || newCustomer.phone.length < 10)) ||
                !veiculoOk
              }
              onClick={() => setStep("horario")}
            >
              {t("common.continue")}
            </Button>
          </div>
        </div>
      )}

      {/* ---------------------------------------------------- 3. horário */}
      {step === "horario" && (
        <div className="space-y-4">
          <Input
            label={t("common.date")}
            type="date"
            value={date}
            onChange={(e) => {
              setDate(e.target.value);
              setSlot(null);
            }}
          />

          {loadingSlots ? (
            <div className="flex justify-center py-8">
              <Spinner />
            </div>
          ) : slots.length === 0 ? (
            <p className="rounded-[2px] border border-line bg-carbon-700 px-4 py-3 text-sm text-steel-muted">
              Nenhum horário livre nesta data para {duration(totalMin)} de serviço.
              Tente outro dia.
            </p>
          ) : (
            <div className="grid grid-cols-4 gap-2 sm:grid-cols-6">
              {slots.map((iso) => (
                <button
                  key={iso}
                  onClick={() => setSlot(iso)}
                  className={`rounded-[2px] border py-2.5 text-sm font-medium tabular-nums transition ${
                    slot === iso
                      ? "border-nitro-500 bg-nitro-500 text-carbon-900"
                      : "border-line hover:border-nitro-500 hover:bg-nitro-500/10"
                  }`}
                >
                  {hhmm(iso)}
                </button>
              ))}
            </div>
          )}

          <ErrorBanner message={error?.message} />

          <div className="flex gap-2">
            <Button variant="secondary" onClick={() => setStep("cliente")}>{t("common.back")}</Button>
            <Button
              className="flex-1"
              disabled={!slot || create.isPending}
              onClick={submit}
            >
              {create.isPending ? (
                <Spinner className="size-4 border-carbon-900/30 border-t-carbon-900" />
              ) : (
                t("modal.bookFor", { amount: money(totalPrice) })
              )}
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
}
